/**
 * OX 퀴즈 태블릿 실행 흐름의 단일 진입점.
 *
 * 현장 QuestTagPage와 CRM 미리보기 전용 화면이 이 컴포넌트를 함께 사용한다. 미리보기용으로
 * 화면을 복제하지 않아 인트로 → 인사말 → 문제 → 정오 영상 → 완료 연출이 운영 태블릿과
 * 달라지는 것을 막는다. 태그·보상 처리는 부모 콜백의 책임이라 이 컴포넌트에는 API 호출이 없다.
 */
import React, { useEffect, useState } from 'react';
import OxQuizRunner from './OxQuizRunner';
import MissionIntroVideoScreen from './MissionIntroVideoScreen';
import OxGreetingScreen from './OxGreetingScreen';

export const OX_PHASE_INTRO = 'intro';
export const OX_PHASE_GREETING = 'greeting';
export const OX_PHASE_QUIZ = 'quiz';

/** OX 퀴즈 시작 영상. 세트 공통 로고를 우선하고 없으면 기존 스팟 영상을 사용한다. */
export function resolveMissionVideoUrl(spot, commonIntroVideoUrl = null) {
  if (!spot || spot.mission_type !== 'ox_quiz') return null;
  const url = [commonIntroVideoUrl, spot.mission_video_url]
    .find((candidate) => typeof candidate === 'string' && candidate.trim());
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
}

/** 인사말 영상 URL — 서버가 `mission_config`에 해석해 넣어준다(계약 C2). */
export function resolveOxGreetingVideoUrl(config) {
  const url = config?.greeting_video_url;
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
}

/** 첫 화면은 마운트 시점에 결정해 영상이 없는 조합에서도 빈 프레임을 만들지 않는다. */
export function initialOxPhase(introVideoUrl, greetingVideoUrl) {
  if (introVideoUrl) return OX_PHASE_INTRO;
  if (greetingVideoUrl) return OX_PHASE_GREETING;
  return OX_PHASE_QUIZ;
}

/** 영상 종료 후 없는 단계를 건너뛰고 갈 다음 단계. */
export function nextOxPhase(phase, greetingVideoUrl) {
  if (phase === OX_PHASE_INTRO && greetingVideoUrl) return OX_PHASE_GREETING;
  return OX_PHASE_QUIZ;
}

export default function OxQuizMissionFlow({
  spot, config, commonIntroVideoUrl, spotName, memberNickname,
  onComplete, onCancel, onVideoAbort,
}) {
  const introVideoUrl = resolveMissionVideoUrl(spot, commonIntroVideoUrl);
  const greetingVideoUrl = resolveOxGreetingVideoUrl(config);
  const [phase, setPhase] = useState(
    () => initialOxPhase(introVideoUrl, greetingVideoUrl),
  );

  // 스팟이나 영상이 교체되면 실행 흐름을 처음부터 다시 시작한다.
  useEffect(() => {
    setPhase(initialOxPhase(introVideoUrl, greetingVideoUrl));
  }, [spot?.id, introVideoUrl, greetingVideoUrl]);

  if (phase === OX_PHASE_INTRO) {
    return (
      <MissionIntroVideoScreen
        ariaLabel="OX 퀴즈 시작 영상"
        logLabel="OX 인트로 영상"
        videoUrl={introVideoUrl}
        onFinished={() => setPhase(nextOxPhase(OX_PHASE_INTRO, greetingVideoUrl))}
        onAbort={onVideoAbort}
      />
    );
  }

  if (phase === OX_PHASE_GREETING) {
    return (
      <OxGreetingScreen
        videoUrl={greetingVideoUrl}
        memberNickname={memberNickname}
        onFinished={() => setPhase(OX_PHASE_QUIZ)}
        onAbort={onVideoAbort}
      />
    );
  }

  return (
    <OxQuizRunner
      config={config}
      spotName={spotName}
      onComplete={onComplete}
      onCancel={onCancel}
      onVideoAbort={onVideoAbort}
    />
  );
}
